import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { extractErrorMessage } from '@/lib/errorMessage';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Invalid or expired reset link. Please request a new one.'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#F8F9FA' }}>
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold text-[#1E293B] mb-2">Invalid Reset Link</h2>
            <p className="text-sm text-[#64748B] mb-4">This link is invalid or has expired.</p>
            <a href="/forgot-password" className="text-sm text-[#6366F1] hover:underline font-semibold">Request a new reset link</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#F8F9FA' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <span className="text-3xl font-bold text-[#6366F1]">BrixLead</span>
          <p className="text-[#64748B] text-sm mt-2">Create a new password</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-8">
          {success ? (
            <div className="text-center space-y-4">
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-[#1E293B]">Password Reset!</h2>
              <p className="text-sm text-[#64748B]">
                Your password has been updated. Redirecting to login...
              </p>
              <a href="/login" className="inline-flex items-center gap-1.5 text-sm text-[#6366F1] hover:underline font-semibold">
                <ArrowLeft className="h-4 w-4" /> Go to login
              </a>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-[#1E293B] mb-2">Set new password</h2>
              <p className="text-sm text-[#64748B] mb-6">Enter your new password below.</p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-sm font-normal text-[#1E293B]">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Min. 6 characters"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    className="h-11"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirmPassword" className="text-sm font-normal text-[#1E293B]">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Re-enter password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    required
                    className="h-11"
                  />
                </div>

                {error && (
                  <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 border border-red-200">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-11 bg-[#6366F1] hover:bg-[#4F46E5] text-white font-semibold rounded-lg"
                  disabled={isLoading}
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Reset Password
                </Button>
              </form>

              <div className="mt-5 text-center">
                <a href="/login" className="inline-flex items-center gap-1.5 text-sm text-[#6366F1] hover:underline font-semibold">
                  <ArrowLeft className="h-4 w-4" /> Back to login
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
